import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { CheckCircle2, DollarSign, Smartphone, Upload, CreditCard, User, Phone, MapPin, FileText, ShieldCheck, Loader2, Sparkles, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const Checkout = () => {
  const { cart, getTotalAmount, clearCart } = useCart();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    notes: '',
    paymentMethod: 'cash'
  });

  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [uploadedScreenshotUrl, setUploadedScreenshotUrl] = useState('');
  const [errors, setErrors] = useState({});
  const [isUploadingSnapshot, setIsUploadingSnapshot] = useState(false);

  useEffect(() => {
    // Fetch Easypaisa payment info
    const fetchPaymentInfo = async () => {
      try {
        const response = await axios.get('/orders/payment-info');
        setPaymentInfo(response.data);
      } catch (error) {
        logger.error('Error fetching payment info:', error);
      }
    };
    fetchPaymentInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Name is required';
    } else if (formData.customerName.trim().length < 2) {
      newErrors.customerName = 'Name must be at least 2 characters';
    }

    if (!formData.customerPhone.trim()) {
      newErrors.customerPhone = 'Phone number is required';
    } else if (!/^03\d{9}$/.test(formData.customerPhone.trim())) {
      newErrors.customerPhone = 'Phone must be 11 digits starting with 03';
    }

    if (!formData.customerAddress.trim()) {
      newErrors.customerAddress = 'Address is required';
    } else if (formData.customerAddress.trim().length < 10) {
      newErrors.customerAddress = 'Please provide a complete address';
    }

    // Payment method validation
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Please select a payment method';
    }

    // Easypaisa screenshot validation
    if (formData.paymentMethod === 'easypaisa' && !uploadedScreenshotUrl) {
      newErrors.paymentScreenshot = 'Payment screenshot is required for Easypaisa';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPaymentScreenshot(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadScreenshot = async () => {
    if (!paymentScreenshot) return null;

    const formDataUpload = new FormData();
    formDataUpload.append('paymentScreenshot', paymentScreenshot);

    try {
      const response = await axios.post('/orders/upload-payment', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.screenshotUrl;
    } catch (error) {
      logger.error('Error uploading screenshot:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }

    // Validate all form fields
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        ...formData,
        items: cart,
        totalAmount: getTotalAmount(),
        paymentScreenshot: uploadedScreenshotUrl || null
      };

      const response = await axios.post('/orders', orderData);
      setOrderNumber(response.data.orderNumber);
      setOrderPlaced(true);
      clearCart();
    } catch (error) {
      logger.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadScreenshot = async () => {
    if (!paymentScreenshot) {
      alert('Please select a screenshot first');
      return;
    }

    setIsUploadingSnapshot(true);

    try {
      const url = await uploadScreenshot();
      setUploadedScreenshotUrl(url);
      setErrors(prev => ({ ...prev, paymentScreenshot: '' })); // Clear error
    } catch (error) {
      alert('Failed to upload screenshot. Please try again.');
    } finally {
      setIsUploadingSnapshot(false);
    }
  };

  if (cart.length === 0 && !orderPlaced) {
    navigate('/cart');
    return null;
  }

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center relative overflow-hidden p-6">
        {/* Confetti or Celebration Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-green-500/10 rounded-full blur-3xl opacity-30" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gold-500/10 rounded-full blur-3xl opacity-30" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full relative z-10"
        >
          <Card className="bg-gray-900/60 border-gold-500/30 backdrop-blur-md shadow-2xl overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-gold-500 to-green-500" />
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping opacity-50" />
                <CheckCircle2 className="text-green-500 w-10 h-10" />
              </div>

              <h2 className="text-3xl font-bold text-white mb-2 font-serif">Order Placed!</h2>
              <p className="text-gray-400 mb-6">Thank you for your order. We're preparing your food!</p>

              <div className="bg-gray-800/50 rounded-xl p-4 mb-8 border border-white/5">
                <p className="text-sm text-gray-500 uppercase tracking-wider font-bold mb-1">Order Number</p>
                <p className="text-4xl font-bold text-gold-500 font-mono tracking-widest">{orderNumber}</p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate(`/track-order?order=${orderNumber}&phone=${formData.customerPhone}`)}
                  className="w-full bg-gold-500 text-black font-bold h-12 shadow-lg hover:bg-gold-400 hover:scale-[1.02] transition-all"
                >
                  Track Order
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/menu')}
                  className="w-full border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white"
                >
                  Back to Menu
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div >
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-900/10 rounded-full blur-3xl opacity-20" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gold-900/10 rounded-full blur-3xl opacity-20" />
      </div>

      <div className="container mx-auto px-6 pt-40 pb-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-serif flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-gold-500" />
            Checkout
          </h1>
          <p className="text-gray-400 mt-2">Complete your order details below.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <form onSubmit={handleSubmit}>
              <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-sm overflow-hidden mb-6">
                <CardHeader className="border-b border-gray-800 pb-4">
                  <CardTitle className="text-xl font-bold text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gold-500" /> Delivery Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="customerName" className="text-gold-100">Full Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                        <Input
                          id="customerName"
                          name="customerName"
                          value={formData.customerName}
                          onChange={handleChange}
                          className={`pl-10 bg-gray-800/50 border-gray-700 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500 ${errors.customerName ? 'border-red-500' : ''}`}
                          placeholder="Enter your full name"
                        />
                      </div>
                      {errors.customerName && <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {errors.customerName}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="customerPhone" className="text-gold-100">Phone Number *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                        <Input
                          id="customerPhone"
                          type="tel"
                          name="customerPhone"
                          value={formData.customerPhone}
                          onChange={handleChange}
                          className={`pl-10 bg-gray-800/50 border-gray-700 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500 ${errors.customerPhone ? 'border-red-500' : ''}`}
                          placeholder="03XXXXXXXXX"
                        />
                      </div>
                      {errors.customerPhone && <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {errors.customerPhone}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="customerAddress" className="text-gold-100">Delivery Address *</Label>
                    <Textarea
                      id="customerAddress"
                      name="customerAddress"
                      value={formData.customerAddress}
                      onChange={handleChange}
                      rows={3}
                      className={`bg-gray-800/50 border-gray-700 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none ${errors.customerAddress ? 'border-red-500' : ''}`}
                      placeholder="House #, Street #, Area, City"
                    />
                    {errors.customerAddress && <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {errors.customerAddress}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes" className="text-gold-100">Special Instructions (Optional)</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={2}
                      className="bg-gray-800/50 border-gray-700 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none"
                      placeholder="Any special requests or dietary requirements?"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-sm overflow-hidden">
                <CardHeader className="border-b border-gray-800 pb-4">
                  <CardTitle className="text-xl font-bold text-white flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-gold-500" /> Payment Method
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {errors.paymentMethod && <p className="text-red-400 text-sm mb-4 flex items-center gap-2 bg-red-500/10 p-3 rounded-lg"><AlertCircle className="w-4 h-4" /> {errors.paymentMethod}</p>}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className={`relative flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 group ${formData.paymentMethod === 'cash'
                      ? 'border-gold-500 bg-gold-500/10'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gold-500/50'
                      }`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash"
                        checked={formData.paymentMethod === 'cash'}
                        onChange={handleChange}
                        className="peer sr-only"
                      />
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${formData.paymentMethod === 'cash' ? 'bg-gold-500 text-black' : 'bg-gray-700 text-gray-400 group-hover:bg-gray-600'}`}>
                          <DollarSign className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-white font-bold text-lg">Cash on Delivery</p>
                          <p className="text-gray-400 text-xs">Pay when you receive</p>
                        </div>
                      </div>
                      {formData.paymentMethod === 'cash' && (
                        <div className="absolute top-4 right-4">
                          <CheckCircle2 className="text-gold-500 w-5 h-5" />
                        </div>
                      )}
                    </label>

                    <label className={`relative flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 group ${formData.paymentMethod === 'easypaisa'
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-gray-700 bg-gray-800/50 hover:border-green-500/50'
                      }`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="easypaisa"
                        checked={formData.paymentMethod === 'easypaisa'}
                        onChange={handleChange}
                        className="peer sr-only"
                      />
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${formData.paymentMethod === 'easypaisa' ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400 group-hover:bg-gray-600'}`}>
                          <Smartphone className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-white font-bold text-lg">Easypaisa</p>
                          <p className="text-gray-400 text-xs">Mobile Account Payment</p>
                        </div>
                      </div>
                      {formData.paymentMethod === 'easypaisa' && (
                        <div className="absolute top-4 right-4">
                          <CheckCircle2 className="text-green-500 w-5 h-5" />
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Easypaisa Details */}
                  <AnimatePresence>
                    {formData.paymentMethod === 'easypaisa' && paymentInfo && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-6 overflow-hidden"
                      >
                        <div className="bg-green-500/5 border border-green-500/30 rounded-xl p-6">
                          <h4 className="text-green-400 font-bold mb-4 flex items-center gap-2">
                            <Smartphone className="w-5 h-5" /> Easypaisa Instructions
                          </h4>

                          <div className="bg-gray-900/80 rounded-lg p-4 mb-6 border border-green-500/20">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-gray-400 text-sm">Account Title</span>
                              <span className="text-white font-bold">{paymentInfo.easypaisaAccountTitle}</span>
                            </div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-gray-400 text-sm">Account Number</span>
                              <span className="text-green-400 font-mono font-bold text-lg tracking-wider">{paymentInfo.easypaisaNumber}</span>
                            </div>
                            <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-2">
                              <span className="text-gray-400 text-sm">Total Amount</span>
                              <span className="text-gold-500 font-bold">Rs. {getTotalAmount().toLocaleString()}</span>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label className="text-green-100 flex items-center gap-2">
                                <Upload className="w-4 h-4" /> Upload Payment Screenshot *
                              </Label>
                              <div className="flex items-center gap-4">
                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleScreenshotChange}
                                  className="bg-gray-800 border-gray-700 text-gray-300 file:bg-green-600 file:text-white file:border-0 file:rounded-md file:mr-4 file:py-1 file:px-2 hover:file:bg-green-500"
                                />
                              </div>
                            </div>

                            {screenshotPreview && (
                              <div className="relative w-full h-48 bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
                                <img src={screenshotPreview} alt="Preview" className="w-full h-full object-contain" />
                              </div>
                            )}

                            {errors.paymentScreenshot && <p className="text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {errors.paymentScreenshot}</p>}

                            {!uploadedScreenshotUrl ? (
                              <Button
                                type="button"
                                onClick={handleUploadScreenshot}
                                disabled={!paymentScreenshot || isUploadingSnapshot}
                                className="w-full bg-green-600 hover:bg-green-500 text-white font-bold"
                              >
                                {isUploadingSnapshot ? (
                                  <span className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" /> Uploading...
                                  </span>
                                ) : (
                                  'Upload Screenshot'
                                )}
                              </Button>
                            ) : (
                              <div className="bg-green-500/20 border border-green-500 rounded-lg p-3 flex items-center justify-center gap-2 text-green-400 font-bold">
                                <CheckCircle2 className="w-5 h-5" /> Screenshot Uploaded!
                              </div>
                            )}

                            <p className="text-xs text-green-200/70 text-center">
                              Your order will be verified by admin after submission.
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </form>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="sticky top-24">
              <Card className="bg-gray-900/80 border-gold-500/30 backdrop-blur-md shadow-xl">
                <CardHeader className="border-b border-gray-800 pb-4">
                  <CardTitle className="text-xl font-bold text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gold-500" /> Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4 mb-6 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {cart.map((item, index) => (
                      <div key={index} className="flex justify-between items-start text-sm">
                        <div className="flex items-start gap-2">
                          <span className="bg-gray-800 text-gray-300 px-1.5 py-0.5 rounded text-xs font-mono">{item.quantity}x</span>
                          <span className="text-gray-300 max-w-[150px] line-clamp-2">{item.name}</span>
                        </div>
                        <span className="text-white font-medium">Rs. {item.itemTotal.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-white/10 pt-4 space-y-2">
                    <div className="flex justify-between text-gray-400 text-sm">
                      <span>Subtotal</span>
                      <span>Rs. {getTotalAmount().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-gray-400 text-sm">
                      <span>Delivery Fee</span>
                      <span className="text-gray-400 font-medium italic">Delivery calculated by Restaurant</span>
                    </div>
                    <div className="flex justify-between items-end pt-2 mt-2 border-t border-white/10">
                      <span className="text-lg font-bold text-white">Total</span>
                      <span className="text-2xl font-bold text-gold-500">Rs. {getTotalAmount().toLocaleString()}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="w-full mt-6 bg-gradient-to-r from-gold-600 to-gold-400 text-black font-bold h-12 text-lg shadow-lg hover:shadow-gold-500/20 hover:scale-[1.02] transition-all"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Placing Order...</>
                    ) : (
                      <><Sparkles className="mr-2 h-5 w-5" /> Place Order</>
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={() => navigate('/cart')}
                    className="w-full mt-2 text-gray-400 hover:text-white"
                  >
                    Back to Cart
                  </Button>

                  <div className="mt-6 flex flex-col items-center gap-2 text-xs text-gray-600 text-center">
                    <div className="flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Secure Checkout</div>
                    <p>Your personal information is encrypted and secure.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;



